import React, { useState, useEffect, useRef, memo } from 'react';
import { Box, Flex, Text, Checkbox, useColorModeValue, Grid, Spinner, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Menu, MenuButton, Button, MenuList, MenuItem } from '@chakra-ui/react';
import { useGetAdsetPlacementsQuery } from 'store/campaigns/adsetFormApi';
import { ChevronDownIcon } from '@chakra-ui/icons';

const placementMapping = {
    facebook: {
        "feed": "Facebook Feed",
        "profile_feed": "Facebook profile feed",
        "marketplace": "Facebook Marketplace",
        "video_feeds": "Facebook video feeds",
        "right_hand_column": "Facebook right column",
        "story": "Facebook Stories",
        "facebook_reels": "Facebook Reels",
        "instream_video": "Facebook in-stream videos",
        "facebook_reels_overlay": "Ads on Facebook Reels",
        "search": "Facebook search results",
        "biz_disco_feed": "Facebook Business Explore"
    },
    instagram: {
        "feed": "Instagram feed",
        "profile_feed": "Instagram profile feed",
        "explore": "Instagram Explore",
        "explore_home": "Instagram Explore home",
        "story": "Instagram Stories",
        "reels": "Instagram Reels",
        "profile_reels": "Instagram profile reels",
        "ig_search": "Instagram search results"
    },
    audience_network: {
        "classic": "Audience Network native, banner and interstitial",
        "rewarded_video": "Audience Network rewarded videos"
    },
    messenger: {
        "story": "Messenger Stories",
        "messenger_home": "Messenger inbox"
    }
};


const PlacementsSelector = memo(function PlacementsSelector({
    buying_type,
    account_id,
    optimization_goal,
    objective,
    billing_event,
    destination_type,
    onPlacementsChange,
    currentAdsetData,
}) {

    const backgroundColor = useColorModeValue('gray.50', 'gray.800');
    const bgColor = useColorModeValue("#f8f6fb", "#1e1530");
    const textColor = useColorModeValue("#421e66", "#b5b5cd");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const hoverBorderColor = useColorModeValue("gray.300", "brand.800");

    const [selectedDevicePlatforms, setSelectedDevicePlatforms] = useState({
        mobile: true,
        desktop: true,
    });

    const [selectedPlatforms, setSelectedPlatforms] = useState({
        facebook: true,
        instagram: true,
        messenger: true,
        audience_network: true,
    });

    const [selectedPositions, setSelectedPositions] = useState({
        facebook: {},
        instagram: {},
        messenger: {},
        audience_network: {},
    });

    const [platformAvailability, setPlatformAvailability] = useState({
        facebook: true,
        instagram: true,
        messenger: true,
        audience_network: true,
    });

    //keep track of previous targeting values
    const previousTargetingRef = useRef({});

    const effectiveDevicePlatforms = Object.keys(selectedDevicePlatforms).filter(
        device => selectedDevicePlatforms[device]
    );

    const shouldSkip = !optimization_goal || !objective || !buying_type || !billing_event || !account_id || !destination_type || (optimization_goal !== 'QUALITY_CALL' && !effectiveDevicePlatforms.length);

    const { data: response, error, isLoading } = useGetAdsetPlacementsQuery(
        {
            optimization_goal,
            objective,
            buying_type,
            billing_event,
            account_id,
            ...(destination_type !== 'AWARENESS' && { destination_type }),
            effective_device_platforms: optimization_goal === 'QUALITY_CALL' ? '' : effectiveDevicePlatforms.join(','),
        },
        { skip: shouldSkip }
    );

    useEffect(() => {
        if (objective === 'OUTCOME_APP_PROMOTION') {
            setSelectedDevicePlatforms({ mobile: true, desktop: false });
        }
    }, [objective]);

    useEffect(() => {
        if (destination_type === 'WHATSAPP') {
            setSelectedDevicePlatforms({ mobile: true, desktop: false });
        }
    }, [destination_type]);    

    // Prefill states from currentAdsetData
    useEffect(() => {
        if (currentAdsetData) {
            const {
                device_platforms,
                publisher_platforms,
                facebook_positions,
                instagram_positions,
                messenger_positions,
                audience_network_positions,
            } = currentAdsetData;

            // Set selected device platforms
            if (device_platforms) {
                const updatedDevicePlatforms = {
                    mobile: device_platforms.includes('mobile'),
                    desktop: device_platforms.includes('desktop'),
                };
                setSelectedDevicePlatforms(updatedDevicePlatforms);
            }

            // Set selected platforms
            const updatedPlatforms = {
                facebook: publisher_platforms ? publisher_platforms.includes('facebook') : false,
                instagram: publisher_platforms ? publisher_platforms.includes('instagram') : false,
                messenger: publisher_platforms ? publisher_platforms.includes('messenger') : false,
                audience_network: publisher_platforms ? publisher_platforms.includes('audience_network') : false,
            };
            setSelectedPlatforms(updatedPlatforms);

            // Initialize updatedPositions object
            const updatedPositions = {};

            // Set selected positions only if available
            if (Array.isArray(facebook_positions)) {
                updatedPositions.facebook = facebook_positions.reduce((acc, pos) => {
                    acc[pos] = true;
                    return acc;
                }, {});
            }
            if (Array.isArray(instagram_positions)) {
                updatedPositions.instagram = instagram_positions.reduce((acc, pos) => {
                    acc[pos] = true;
                    return acc;
                }, {});
            }
            if (Array.isArray(messenger_positions)) {
                updatedPositions.messenger = messenger_positions.reduce((acc, pos) => {
                    acc[pos] = true;
                    return acc;
                }, {});
            }
            if (Array.isArray(audience_network_positions)) {
                updatedPositions.audience_network = audience_network_positions.reduce((acc, pos) => {
                    acc[pos] = true;
                    return acc;
                }, {});
            }
            setSelectedPositions(updatedPositions);
        }
    }, [currentAdsetData]);

    useEffect(() => {
        if (response?.data) {
            const placements = response.data;
            const platformNames = ['facebook', 'instagram', 'messenger', 'audience_network'];

            const initialSelectedPositions = platformNames.reduce((acc, platform) => {
                const positions = placements[`effective_${platform}_positions`] || [];
                const updatedPositionsForPlatform = {};

                if (currentAdsetData) {
                    // If currentAdsetData exists, filter based on available positions
                    const currentPositions = currentAdsetData[`${platform}_positions`] || [];
                    currentPositions.forEach(pos => {
                        if (positions.includes(pos)) {
                            updatedPositionsForPlatform[pos] = true;
                        }
                    });
                } else {
                    // select all available positions
                    positions.forEach(pos => {
                        updatedPositionsForPlatform[pos] = true;
                    });
                }

                acc[platform] = updatedPositionsForPlatform;
                return acc;
            }, {});

            // Update platform availability based on data
            const availability = platformNames.reduce((acc, platform) => {
                acc[platform] = (placements[`effective_${platform}_positions`] || []).length > 0;
                return acc;
            }, {});

            setSelectedPositions(initialSelectedPositions);
            setPlatformAvailability(availability);
        }
    }, [response, currentAdsetData]);


    useEffect(() => {
        const targeting = {
            publisher_platforms: Object.keys(selectedPlatforms).filter(platform => selectedPlatforms[platform]),
            ...Object.keys(selectedPositions).reduce((acc, platform) => {
                acc[`${platform}_positions`] = Object.keys(selectedPositions[platform]).filter(position => selectedPositions[platform][position]);
                return acc;
            }, {}),
        };

        if (optimization_goal !== 'QUALITY_CALL') {
            targeting.device_platforms = effectiveDevicePlatforms;
        }

        // Only call onPlacementsChange if any change
        const newTargeting = JSON.stringify(targeting);
        if (newTargeting !== JSON.stringify(previousTargetingRef.current)) {
            previousTargetingRef.current = targeting; // Saving the latest targeting
            onPlacementsChange(targeting);
            //console.log(targeting);

        }

    }, [selectedDevicePlatforms, selectedPlatforms, selectedPositions, optimization_goal, effectiveDevicePlatforms]);


    if (isLoading) {
        return <Flex p={2}>
            <Spinner size={'sm'} />
        </Flex>
    }

    if (error) {
        return <Flex p={2}>
            <Text color="red.500" fontSize={'12px'} textAlign={'justify'}>Error fetching placements: {error.data.message}</Text>
        </Flex>
    }

    const placements = response?.data || {};

    const getPlacementName = (platform, key) => {

        if (placementMapping[platform]) {

            return placementMapping[platform][key] || key;
        } else {
            return key;
        }
    };


    // Handle device checkbox changes
    const handleDeviceChange = (device) => {
        setSelectedDevicePlatforms(prev => {
            const newState = { ...prev, [device]: !prev[device] };
            if (!newState[device]) {
                const otherDevice = device === 'mobile' ? 'desktop' : 'mobile';
                newState[otherDevice] = true;
            }
            return newState;
        });
    };

    const handlePlatformChange = (platform) => {
        setSelectedPlatforms(prev => {
            const newState = { ...prev, [platform]: !prev[platform] };
            const positions = placements[`effective_${platform}_positions`] || [];
            setSelectedPositions(prevPositions => ({
                ...prevPositions,
                [platform]: positions.reduce((acc, pos) => ({
                    ...acc,
                    [pos]: newState[platform],
                }), {}),
            }));
            return newState;
        });
    };

    const handlePositionChange = (platform, position) => {
        setSelectedPositions(prev => ({
            ...prev,
            [platform]: {
                ...prev[platform],
                [position]: !prev[platform][position],
            },
        }));
    };

    const formatPlatformName = (platform) => {
        return platform.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };


    const convertToSentenceCase = (text) => {
        return text
            .split('_') // Split the text at underscores
            .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
            .join(' '); // Join the words with spaces
    };

    const getSelectedLabel = () => {
        const { mobile, desktop } = selectedDevicePlatforms;
        if (mobile && desktop) return 'All devices (recommended)';
        if (mobile) return 'Mobile';
        if (desktop) return 'Desktop';
        return 'Select Devices';
      };
    
      const isDisabled = (device) => {
        return (
          (device === 'desktop' && objective === 'OUTCOME_APP_PROMOTION') ||
          (device === 'mobile' ? !selectedDevicePlatforms['desktop'] : !selectedDevicePlatforms['mobile'])
        );
      };

    return (
        <Box p={4} borderRadius="md" bg={backgroundColor} mb={4}>
            {/* Placements Header */}
            <Flex justifyContent="space-between" alignItems="center" mb={4}>
                <Text fontWeight="bold" fontSize="lg">Placements</Text>
            </Flex>

            {/* Devices section */}
            {optimization_goal !== 'QUALITY_CALL' && (
                <Box mb="20px">
                    <Text fontWeight="bold" mb={2}>Devices</Text>
                    <Menu closeOnSelect={false}>
                        <MenuButton
                            as={Button}
                            rightIcon={<ChevronDownIcon boxSize={5} />}
                            w="240px"
                            size="sm"
                            fontWeight={'normal'}
                            variant="solid"
                            border="1px solid"
                            borderRadius="md"
                            bg={bgColor}
                            color={textColor}
                            borderColor={borderColor}
                            _hover={{
                                bg: bgColor,
                                color: textColor,
                                borderColor: hoverBorderColor,
                            }}
                            _active={{
                                bg: bgColor,
                                color: textColor,
                                borderColor: hoverBorderColor,
                            }}
                            >
                            {getSelectedLabel()}
                        </MenuButton>
                        <MenuList>
                            {['mobile', 'desktop'].map(device => (
                            <MenuItem key={device} closeOnSelect={false} py={1}>
                                <Checkbox
                                    isChecked={!!selectedDevicePlatforms[device]}
                                    onChange={() => handleDeviceChange(device)}
                                    isDisabled={isDisabled(device)}
                                    size={'sm'}
                                    colorScheme={'brand'}
                                >
                                {device.charAt(0).toUpperCase() + device.slice(1)}
                                </Checkbox>
                            </MenuItem>
                            ))}
                        </MenuList>
                    </Menu>
                </Box>
            )}

            {/* Platforms */}
            <Box mb={'20px'}>
                <Text fontWeight={'bold'} mb={2}>Platforms</Text>
                <Grid templateColumns="repeat(2, 200px)" gap={2} mb={4}>
                    {['facebook', 'instagram', 'messenger', 'audience_network'].map(platform => (
                        <Checkbox
                            key={platform}
                            colorScheme="brand"
                            isChecked={!!selectedPlatforms[platform] && platformAvailability[platform]}
                            isDisabled={!platformAvailability[platform]}
                            onChange={() => handlePlatformChange(platform)}
                        >
                            {convertToSentenceCase(platform)}
                        </Checkbox>
                    ))}
                </Grid>
            </Box>

            {/* Position Sections with Accordion */}
            <Text fontWeight={'bold'} mb={2}>Placements</Text>
            <Accordion allowToggle>
                {['facebook', 'instagram', 'messenger', 'audience_network'].map(platform => {
                    // Get the positions for the platform
                    const platformPositions = selectedPositions[platform] || {};
                    const availablePositions = placements[`effective_${platform}_positions`] || [];

                    return (
                        <AccordionItem key={platform}>
                            <AccordionButton>
                                <Box flex="1" textAlign="left">
                                    <Text fontSize={'14px'} fontWeight="bold">{formatPlatformName(platform)}</Text>
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={4}>
                                {availablePositions.length > 0 ? (
                                    <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                        {availablePositions.map(position => (
                                            <Box
                                                key={position}
                                                // bg="white"
                                                p={2}
                                                borderRadius="md"
                                                borderWidth="1px"
                                                borderColor="gray.200"
                                            >
                                                <Flex justifyContent="space-between" alignItems="center">
                                                    <Text fontSize={'14px'}>{getPlacementName(platform, position)}</Text>
                                                    <Checkbox
                                                        colorScheme="brand"
                                                        isChecked={selectedPlatforms[platform] && platformPositions[position]} // Check if the platform is selected and the specific position is selected
                                                        onChange={() => handlePositionChange(platform, position)} // Handle checkbox change
                                                    />
                                                </Flex>
                                            </Box>
                                        ))}
                                    </Grid>
                                ) : (
                                    <Text color="gray.500">No placements available for {formatPlatformName(platform)}.</Text>
                                )}
                            </AccordionPanel>
                        </AccordionItem>
                    );
                })}
            </Accordion>

        </Box>
    );
});

export default PlacementsSelector;
